import _ from 'lodash';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';

import { CheckCircleTwoTone } from '@ant-design/icons';
import { Spin } from 'antd';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { capitalizeFirstLetter } from 'v3/utils/formaters';

const NoIssuesSection = styled.div`
  display: flex;
  align-items: center;
  p {
    margin-bottom: 0px;
    font-size: 14px;
  }
  i {
    font-size: 36px;
    margin-right: 12px;
  }
`;

const DoughnutChart = ({ chart, loaded, data, onClick, click }) => {
  const processedData = useMemo(() => {
    if (_.isEmpty(data?.value)) return [];

    return data.value.map((d) => ({
      name: capitalizeFirstLetter(d.name),
      color: d.color,
      y: d.y,
      units: data.units || '',
      events: {
        click: function () {
          if (click) {
            click(d.name);
          }
        },
      },
    }));
  }, [data, click]);

  if (!loaded) {
    return <Spin />;
  }

  if (
    _.isEmpty(processedData) ||
    _.isEmpty(_.filter(processedData, (d) => d.y !== 0))
  ) {
    return (
      <NoIssuesSection>
        <CheckCircleTwoTone />
        <p>
          {data.emptyMessage ||
            `Your fleet has no ${data.seriesName ? capitalizeFirstLetter(data.seriesName) : ''}!`}
        </p>
      </NoIssuesSection>
    );
  }

  const chartOptions = {
    chart: {
      height: chart.height,
      margin: chart.margin,
      type: 'pie',
    },
    title: { text: undefined },
    exporting: { enabled: false },
    credits: { enabled: false },
    plotOptions: {
      pie: {
        shadow: false,
        cursor: 'pointer',
        dataLabels: { enabled: false },
        showInLegend: true,
        events: {
          click: function () {
            if (onClick) {
              onClick();
            }
          },
        },
      },
    },
    legend: {
      align: chart.legend.align,
      layout: chart.legend.layout,
      verticalAlign: chart.legend.verticalAlign,
      width: chart.legend.width,
      x: chart.legend.x,
      y: chart.legend.y,
      labelFormatter: function () {
        return `${this.name}: ${this.y} ${this.units}`;
      },
    },
    tooltip: { valueSuffix: ` ${data.units || ''}` },
    series: [
      {
        showInLegend: true,
        name: data.seriesName ? capitalizeFirstLetter(data.seriesName) : '',
        colorByPoint: true,
        size: chart.size,
        innerSize: '50%',
        data: processedData,
      },
    ],
  };

  return <HighchartsReact highcharts={Highcharts} options={chartOptions} />;
};

DoughnutChart.propTypes = {
  onClick: PropTypes.func,
  click: PropTypes.func,
  chart: PropTypes.object.isRequired,
  loaded: PropTypes.bool.isRequired,
  data: PropTypes.shape({
    seriesName: PropTypes.string,
    value: PropTypes.array.isRequired,
    units: PropTypes.string,
    emptyMessage: PropTypes.string,
  }),
};

export default withRouter(observer(DoughnutChart));
